import React, { useCallback, useEffect } from 'react';

import { dataLayerPush } from '@r1-frontend/analytics/dataLayerPush';
import { TInitializationProps } from '@r1-frontend/service-engineer-visit/actions/thunk/initialization';
import CommentContainer from '@r1-frontend/service-engineer-visit/containers/CommentContainer';
import DateSelectionContainer from '@r1-frontend/service-engineer-visit/containers/DateSelectionContainer';
import DeliveryOptionsContainer from '@r1-frontend/service-engineer-visit/containers/DeliveryOptionsContainer';
import NotAvailableTimePopupContainer from '@r1-frontend/service-engineer-visit/containers/NotAvailableTimePopupContainer';
import SubmitButtonContainer from '@r1-frontend/service-engineer-visit/containers/SubmitButtonContainer';
import TimeSelectionContainer from '@r1-frontend/service-engineer-visit/containers/TimeSelectionContainer';
import { useContextDispatch, useContextSelector } from '@r1-frontend/service-engineer-visit/hooks';
import areSlotsEmpty from '@r1-frontend/service-engineer-visit/selectors/areSlotsEmpty';
import getErrorMessage from '@r1-frontend/service-engineer-visit/selectors/getErrorMessage';
import getLoadingState from '@r1-frontend/service-engineer-visit/selectors/getLoadingState';
import { TDeliveryOption } from '@r1-frontend/service-engineer-visit/types/TDeliveryOption';
import { TGetTimeSlots } from '@r1-frontend/service-engineer-visit/types/TGetTimeSlots';
import { TReturnType } from '@r1-frontend/service-engineer-visit/types/TReturnType';

import { SkeletonTheme, WithSkeletonContext } from '@r1-frontend/ui-react/components/loaders/Skeleton';
import { BorderRadius } from '@r1-frontend/ui-react/tokens/borderRadius';
import { EVENTS } from '@r1-frontend/shared/constants/analytics';

import {
    initialization,
} from '../actions';

import * as S from './styled';

type TDeliveryTypeProps = {
    onDeliveryOptionSelected?: (deliveryOption: TDeliveryOption) => void,
    deliveryOptions: TDeliveryOption[],
} | {
    onDeliveryOptionSelected?: never,
    deliveryOptions?: never,
};

export type TProps = {
    onConfirm: (result: TReturnType | null, msg?: string) => void,
    onClickConfirm: () => void, // TODO, требуется другое решение. (сейчас используется для отправки события клика по кнопке подтвердить в аналитику)
    getTimeSlotsFunc: TGetTimeSlots,
    withComment?: boolean,
    onUpdateLoading?: () => void,
    domain: string,
}

type DateOfServiceEngineerVisitContainerWithAgreementIdProps =
    TProps
    & TDeliveryTypeProps
    & Omit<TInitializationProps, 'deliveryOptions' | 'getTimeSlotsFunc'>;

const DateOfServiceEngineerVisit = ({
    getTimeSlotsFunc,
    withComment,
    onConfirm,
    onClickConfirm,
    onDeliveryOptionSelected = () => true,
    domain,
    onUpdateLoading,
    ...initializationObject
}: DateOfServiceEngineerVisitContainerWithAgreementIdProps): JSX.Element => {
    const { loading, isSlotsEmpty, errorMessage } = useContextSelector(state => ({
        loading: getLoadingState(state),
        isSlotsEmpty: areSlotsEmpty(state),
        errorMessage: getErrorMessage(state),
    }));
    const dispatch = useContextDispatch();

    const sendAnalytics = useCallback(({ action, label }: { action: string, label?: string }) => {
        return dataLayerPush({
            event: EVENTS.UAevent,
            category: initializationObject?.gaCategory || '',
            action,
            label: label || initializationObject?.gaLabel || 'start',
        });
    }, [initializationObject?.gaCategory]);

    useEffect(() => {
        dispatch(
            initialization(
                {
                    ...initializationObject,
                    getTimeSlotsFunc,
                    withComment,
                },
                domain,
            ),
        );
    }, []);

    // todo: вынести в экшн
    useEffect(() => {
        if (!loading) {
            onUpdateLoading && onUpdateLoading();
            isSlotsEmpty && onConfirm(null, errorMessage);
        }
    }, [loading, isSlotsEmpty, onConfirm, errorMessage, onUpdateLoading]);

    return (
        <SkeletonTheme borderRadius={BorderRadius.componentRadius}>
            <WithSkeletonContext isLoading={loading}>
                <S.MainWrapper>
                    <S.InnerWrapper>
                        {(initializationObject.deliveryOptions && initializationObject.deliveryOptions.length > 0) && (
                            <DeliveryOptionsContainer
                                domain={domain}
                                sendAnalytics={sendAnalytics}
                                onDeliveryOptionSelected={onDeliveryOptionSelected}
                                gaLabel={initializationObject?.gaLabel}
                                productId={initializationObject?.productId}
                            />
                        )}

                        <DateSelectionContainer
                            getTimeSlotsFunc={getTimeSlotsFunc}
                            sendAnalytics={sendAnalytics}
                            productId={initializationObject?.productId}
                            gaLabel={initializationObject?.gaLabel}
                        />

                        <TimeSelectionContainer
                            sendAnalytics={sendAnalytics}
                            gaLabel={initializationObject?.gaLabel}
                        />

                        <CommentContainer />
                    </S.InnerWrapper>
                </S.MainWrapper>

                <SubmitButtonContainer
                    domain={domain}
                    onConfirm={onConfirm}
                    onClickConfirm={onClickConfirm}
                    getTimeSlotsFunc={getTimeSlotsFunc}
                    gaLabel={initializationObject?.gaLabel}
                    productId={initializationObject?.productId}
                />
            </WithSkeletonContext>
            <NotAvailableTimePopupContainer />
        </SkeletonTheme>
    );
};

export default DateOfServiceEngineerVisit;
